/**
 * `AttackCard` — COMMS.app's attack result card (`comms-attack-reaction-cards` Phase 3), the
 * design's "recommended" column. Collapsed, one row per shot says who hit whom, the outcome and the
 * damage:
 *
 *     Little Red Bus ⌖ Goblin-3                 [HIT] 5 KIN
 *     Anti-Materiel Rifle · 21 beats EVA 8
 *     [+IMPAIRED] [−1 STRUCTURE]
 *
 * **The whole glance is one button** (`aria-expanded`) that opens the full breakdown: the
 * narrator's story line over its receipt, laid out as a label / explanation / number grid off the
 * structured receipt cells (`narrateResolution({ structured: true })`). The breakdown is always in
 * the DOM, `hidden` while collapsed, so anything that reads the card's text keeps reading it. It
 * starts collapsed for every viewer, GM included (settled item 5); open state is per-viewer UI
 * state, never persisted.
 *
 * Three layouts over one {@link ShotView}:
 *  - a **single target**: `Actor ⌖ Target` headline row (a sword for melee, a crosshair otherwise,
 *    see {@link AttackMark}), `<weapon> · 21 beats EVA 8` under it;
 *  - an **area attack**: `Actor · 3 TARGETS`, `Howitzer (Blast 2)`, then one row per caught target,
 *    never a cross-target total (`projects/completed/aoe-attack-headline.md` D1);
 *  - a **multi-shot group** ({@link AttackGroupCard}): a `BARRAGE · CARAPACE · 2 SHOTS` header with
 *    per-target damage sums, over numbered shot rows that each open on their own.
 *
 * **Pure render of the record.** Every number comes off {@link AttackGlance} (`summarizeAttackGlance`)
 * or the narrator's receipt; nothing is re-derived here except the group header's per-target sum,
 * which adds numbers the glance already carries. Redaction is upstream: a class-tier hostile's
 * record arrives with its weapon dropped and its sources reading the sentinel, and this card shows
 * exactly that. The glance never shows a remaining HP or structure figure (settled item 3).
 *
 * Palette: the `--ac-*` tokens in `AttackCard.css`, never `--gms-accent`; every text/background pair
 * is pinned ≥ 4.5:1 by `attackPalette.contrast.test.ts`.
 */
import { useId, useState, type ReactNode } from "react";
import { faCrosshairs } from "@fortawesome/free-solid-svg-icons";
import { Icon } from "../Icon";
import { splitResolutionNotes } from "@lancer-vtt/shared";
import type { AttackGlance, GlanceChip, GlanceLink, GlanceReaction, GlanceTarget, ReceiptRow } from "@lancer-vtt/rules";
import { damageTypeCode } from "./damageTypeCode";
import "./AttackCard.css";

/** Everything one resolution's card shows: the glance, and the breakdown behind it. */
export type ShotView = {
  glance: AttackGlance;
  /** The narrator's story sentence — the breakdown's first line. */
  story: string;
  /** The narrator's structured receipt. */
  receipt: readonly ReceiptRow[];
  warnings?: readonly string[];
  /** This attack was an Overwatch reaction. */
  overwatch?: boolean;
  /** The GM forced this attack past the rules (`decisions/0209`): the card wears a GM OVERRIDE chip, for everyone. */
  forced?: boolean;
};

const STAMP_CLASS: Record<GlanceTarget["outcome"], string> = { HIT: "ac-stamp-hit", CRIT: "ac-stamp-crit", MISS: "ac-stamp-miss" };

function Stamp({ outcome }: { outcome: GlanceTarget["outcome"] }) {
  return <span className={`ac-stamp ${STAMP_CLASS[outcome]}`}>{outcome}</span>;
}

/** The big damage figure(s): `5 KIN`, or `3 KIN 2 BURN` for two types; a dash when nothing was dealt. */
function Damage({ target }: { target: GlanceTarget }) {
  if (target.damage.length === 0) {
    return (
      <span className="ac-dmg-none" aria-label="no damage">
        —
      </span>
    );
  }
  return (
    <span className="ac-dmgs">
      {target.damage.map((d) => (
        <span key={d.dtype} className="ac-dmg">
          <span className="ac-dmg-n">{d.total}</span> <span className="ac-dmg-type">{damageTypeCode(d.dtype)}</span>
        </span>
      ))}
    </span>
  );
}

/** `21 beats EVA 8`, or `50% miss chance` for a shot the Invisible coin sent wide before any d20. */
function toHitText(t: GlanceTarget): string | undefined {
  if (t.toHit !== undefined) return `${t.toHit.total} ${t.toHit.verdict} ${t.toHit.vs} ${t.toHit.def}`;
  if (t.missChance !== undefined) return `${Math.round(t.missChance * 100)}% miss chance`;
  return undefined;
}

/** What fired, as the second line names it: `Anti-Materiel Rifle`, `Invade · Fragment Signal`, `Howitzer (Blast 2)`. */
function sourceText(view: ShotView): string | undefined {
  const { weapon, tech } = view.glance;
  const parts: string[] = [];
  if (view.overwatch) parts.push("Overwatch");
  if (tech !== undefined) {
    if (tech.action !== undefined) parts.push(tech.action);
    if (tech.option !== undefined) parts.push(tech.option);
  } else if (weapon !== undefined) {
    parts.push(weapon.area !== undefined ? `${weapon.name} (${weapon.area})` : weapon.name);
  }
  return parts.length > 0 ? parts.join(" · ") : undefined;
}

function Chips({ chips, self }: { chips: readonly GlanceChip[]; self?: boolean }) {
  if (chips.length === 0) return null;
  return (
    <span className="ac-chips">
      {chips.map((c, i) => (
        <span key={i} className={`ac-chip ac-chip-${c.kind}`}>
          {self ? `SELF ${c.text}` : c.text}
        </span>
      ))}
    </span>
  );
}

/** `ACC +1` / `DIFF 2` — the provenance row's label, off the recorded net. */
function accuracyLabel(net: number): string {
  return net >= 0 ? `ACC +${net}` : `DIFF ${-net}`;
}

/**
 * The receipt as the design's grid: a 58px label column, the explanation, and the running number
 * right-aligned. A row with structured cells uses them; one without (RESULT, STATUS, a sub row)
 * puts its `text` in the middle column. A per-target divider spans the grid. Named accuracy
 * sources are unbreakable chips, and a no-op RESIST (`1 → 1`) reads muted.
 */
function ReceiptGrid({ rows }: { rows: readonly ReceiptRow[] }) {
  return (
    <div className="ac-grid">
      {rows.map((row, i) => {
        if (row.divider) {
          return (
            <div key={i} className="ac-row ac-row-divider">
              {row.text}
            </div>
          );
        }
        const cls = `ac-row${row.sub ? " ac-row-sub" : ""}${row.noChange ? " ac-row-noop" : ""}`;
        if (row.accuracy !== undefined) {
          return (
            <div key={i} className={`${cls} ac-row-acc`}>
              <span className="ac-cell-label">{accuracyLabel(row.accuracy.net)}</span>
              <span className="ac-cell-detail">
                {row.accuracy.sources.map((s, j) => (
                  <span key={j} className="ac-source">
                    {s}
                  </span>
                ))}
              </span>
              <span className="ac-cell-value" />
            </div>
          );
        }
        return (
          <div key={i} className={cls}>
            <span className="ac-cell-label">{row.label}</span>
            <span className="ac-cell-detail">{row.detail ?? row.text}</span>
            <span className="ac-cell-value">{row.value ?? ""}</span>
          </div>
        );
      })}
    </div>
  );
}

function Breakdown({ view, id, open }: { view: ShotView; id: string; open: boolean }) {
  return (
    <div className="ac-breakdown" id={id} hidden={!open}>
      <p className="ac-story">{view.story}</p>
      <ReceiptGrid rows={view.receipt} />
    </div>
  );
}

/** The advisories under a shot (`decisions/0113`), each with the ▲; a narration note (`resolutionNote`) reads as a plain muted line. */
function Warnings({ warnings: lines }: { warnings?: readonly string[] }) {
  if (lines === undefined || lines.length === 0) return null;
  const { warnings, notes } = splitResolutionNotes(lines);
  return (
    <ul className="ac-warnings">
      {notes.map((n, i) => (
        <li key={`n${i}`} className="ac-note">
          {n}
        </li>
      ))}
      {warnings.map((w, i) => (
        <li key={i} className="ac-warning">
          {w}
        </li>
      ))}
    </ul>
  );
}

/** One caught target's row in an area layout: `Ronin   19 beats 10   [CRIT]   9 EXP`. */
function AreaRow({ target }: { target: GlanceTarget }) {
  const hit = target.toHit !== undefined ? `${target.toHit.total} ${target.toHit.verdict} ${target.toHit.def}` : (toHitText(target) ?? "");
  return (
    <>
      <span className="ac-area-row">
        <span className="ac-area-name">{target.target.name}</span>
        <span className="ac-area-hit">{hit}</span>
        <span className="ac-area-stamp">
          <Stamp outcome={target.outcome} />
        </span>
        <span className="ac-area-dmg">
          <Damage target={target} />
        </span>
      </span>
      <Chips chips={target.chips} />
    </>
  );
}

/**
 * One resolution's toggle + breakdown + warnings. `title` is the headline's left side (the
 * standalone card's `Actor ⌖ Target`, a group row's weapon name). `withSource` puts what fired on
 * the second line; a group row already names it in its title.
 */
function Shot({ view, title, withSource }: { view: ShotView; title: ReactNode; withSource: boolean }) {
  const [open, setOpen] = useState(false);
  const id = useId();
  const { glance } = view;
  const area = glance.targets.length > 1;
  const single = glance.targets[0];
  const source = withSource ? sourceText(view) : undefined;
  const hit = !area && single !== undefined ? toHitText(single) : undefined;
  const sub = [source, hit].filter((s): s is string => s !== undefined).join(" · ");
  return (
    <>
      <button type="button" className="ac-toggle" aria-expanded={open} aria-controls={id} onClick={() => setOpen((o) => !o)}>
        {view.forced && <span className="ac-override">GM OVERRIDE</span>}
        <span className="ac-head">
          <span className="ac-title">{title}</span>
          {area ? (
            <span className="ac-count">{glance.targets.length} TARGETS</span>
          ) : (
            single !== undefined && (
              <span className="ac-result">
                <Stamp outcome={single.outcome} />
                <Damage target={single} />
              </span>
            )
          )}
        </span>
        {sub !== "" && <span className="ac-sub">{sub}</span>}
        {area ? (
          <span className="ac-area">
            {glance.targets.map((t) => (
              <AreaRow key={t.target.id} target={t} />
            ))}
          </span>
        ) : (
          single !== undefined && <Chips chips={single.chips} />
        )}
        <Chips chips={glance.self} self />
        <ReactionRows reactions={glance.reactions} />
        {glance.links?.map((link, i) => <LinkBlock key={i} link={link} />)}
      </button>
      <Breakdown view={view} id={id} open={open} />
      <Warnings warnings={view.warnings} />
    </>
  );
}

/**
 * The mark between attacker and target: a sword for a melee attack, a crosshair for anything else
 * (ranged, tech, or a class-tier hostile whose weapon the record redacts). Font Awesome's free set
 * has no sword, so it is drawn here, pointing at the target, on the same 1em `currentColor` box as
 * {@link Icon}.
 */
function AttackMark({ melee }: { melee: boolean }) {
  return (
    <span className="ac-arrow" role="img" aria-label="attacks">
      {melee ? (
        <svg className="icon" viewBox="0 0 24 24" aria-hidden="true" focusable="false" data-icon="sword">
          <path fill="currentColor" d="M9 10.5h10.5L23 12l-3.5 1.5H9z M7 6h2v12H7z M3 11h4v2H3z M1 12a1.5 1.5 0 1 0 3 0a1.5 1.5 0 1 0-3 0z" />
        </svg>
      ) : (
        <Icon icon={faCrosshairs} />
      )}
    </span>
  );
}

/**
 * One chain link under the primary attack (`decisions/0210`): what caused it and the random pick
 * (`AGGRESSION · d3 = 1`), then who it made act on whom, its outcome and its chips. The primary
 * row above reads only its own steps, so the chain's consequences sit under the unit that caused
 * them.
 */
function LinkBlock({ link }: { link: GlanceLink }) {
  const { glance } = link;
  const single = glance.targets.length === 1 ? glance.targets[0] : undefined;
  return (
    <span className="ac-link">
      <span className="ac-link-cause">{[link.cause.toUpperCase(), link.pick].filter((p) => p !== undefined).join(" · ")}</span>
      <span className="ac-head">
        <span className="ac-title ac-link-title">
          {glance.actor.name}
          {single !== undefined && (
            <>
              <AttackMark melee={glance.weapon?.melee === true} />
              {single.target.name}
            </>
          )}
        </span>
        {single !== undefined ? (
          <span className="ac-result">
            <Stamp outcome={single.outcome} />
            <Damage target={single} />
          </span>
        ) : (
          <span className="ac-count">{glance.targets.length} TARGETS</span>
        )}
      </span>
      {single !== undefined ? (
        <Chips chips={single.chips} />
      ) : (
        <span className="ac-area">
          {glance.targets.map((t) => (
            <AreaRow key={t.target.id} target={t} />
          ))}
        </span>
      )}
      <Chips chips={glance.self} self />
      <ReactionRows reactions={glance.reactions} />
    </span>
  );
}

/** The reactions that interrupted an attack (`chained-resolution-sequence` 4.3): `BRACE · pc1  +BRACED`. */
function ReactionRows({ reactions }: { reactions?: readonly GlanceReaction[] }) {
  if (reactions === undefined || reactions.length === 0) return null;
  return (
    <>
      {reactions.map((r, i) => (
        <span key={i} className="ac-reaction">
          <span className="ac-reaction-cause">{r.cause.toUpperCase()}</span>
          <span className="ac-reaction-actor">{r.actor.name}</span>
          <Chips chips={r.chips} />
        </span>
      ))}
    </>
  );
}

/** A standalone attack card: one resolution, single-target or area. */
export function AttackCard({ view }: { view: ShotView }) {
  const { glance } = view;
  const single = glance.targets.length === 1 ? glance.targets[0] : undefined;
  const title =
    single !== undefined ? (
      <>
        {glance.actor.name}
        <AttackMark melee={glance.weapon?.melee === true} />
        {single.target.name}
      </>
    ) : (
      glance.actor.name
    );
  return (
    <div className="ac" role="status">
      <Shot view={view} title={title} withSource />
    </div>
  );
}

/** `5 TO ASSAULT · 3 TO GOBLIN-3` — each target's damage summed across the group's shots, in first-hit order. */
export function groupDamageSummary(views: readonly ShotView[]): string {
  const totals = new Map<string, { name: string; total: number }>();
  for (const v of views) {
    for (const t of v.glance.targets) {
      const dealt = t.damage.reduce((sum, d) => sum + d.total, 0);
      if (dealt <= 0) continue;
      const cur = totals.get(t.target.id) ?? { name: t.target.name, total: 0 };
      totals.set(t.target.id, { name: cur.name, total: cur.total + dealt });
    }
  }
  if (totals.size === 0) return "NO DAMAGE";
  return [...totals.values()].map((t) => `${t.total} TO ${t.name.toUpperCase()}`).join(" · ");
}

/** The shot row's title: what fired, and whom it hit when the group's shots went at different targets. */
function shotTitle(view: ShotView, mixedTargets: boolean): ReactNode {
  const { weapon, targets } = view.glance;
  const name = weapon !== undefined ? (weapon.area !== undefined ? `${weapon.name} (${weapon.area})` : weapon.name) : "Shot";
  const only = targets.length === 1 ? targets[0] : undefined;
  if (!mixedTargets || only === undefined) return name;
  return (
    <>
      {name}
      <AttackMark melee={view.glance.weapon?.melee === true} />
      {only.target.name}
    </>
  );
}

/**
 * A multi-shot action's shots under one header (3.3): `BARRAGE · CARAPACE · 2 SHOTS` with the
 * per-target damage sums on the right, then one numbered row per shot, each its own toggle. The
 * caller decides what belongs in the group (`commsShotGroups.ts`); a group of one is never passed
 * here — it renders as a plain {@link AttackCard}.
 */
export function AttackGroupCard({
  views,
  shotClassName,
  isUndone,
  headClassName,
}: {
  views: readonly ShotView[];
  /**
   * An extra class on each shot row — COMMS marks every row as one feed entry's card, so a count of
   * cards stays a count of entries. A function picks it per shot (COMMS strikes an undone shot).
   */
  shotClassName?: string | ((shotIndex: number) => string);
  /** Whether a shot was walked back (COMMS undo): an undone shot's damage leaves the header sum. */
  isUndone?: (shotIndex: number) => boolean;
  /** An extra class on the header — COMMS strikes it once every shot is undone. */
  headClassName?: string;
}) {
  const first = views[0]!.glance;
  const action = first.shot?.action ?? "barrage";
  const targetIds = new Set(views.flatMap((v) => v.glance.targets.map((t) => t.target.id)));
  return (
    <div className="ac ac-group" role="status">
      <div className={headClassName !== undefined ? `ac-group-head ${headClassName}` : "ac-group-head"}>
        <span className="ac-group-label">
          {action.toUpperCase()} · {first.actor.name.toUpperCase()}
        </span>
        <span className="ac-group-sub">
          {views.length} SHOTS · <span className="ac-group-sum">{groupDamageSummary(isUndone !== undefined ? views.filter((_, i) => !isUndone(i)) : views)}</span>
        </span>
      </div>
      {views.map((view, i) => (
        <div key={i} className={shotClassName !== undefined ? `ac-shot ${typeof shotClassName === "function" ? shotClassName(i) : shotClassName}` : "ac-shot"}>
          <span className="ac-shot-n">{i + 1}</span>
          <div className="ac-shot-body">
            <Shot view={view} title={shotTitle(view, targetIds.size > 1)} withSource={false} />
          </div>
        </div>
      ))}
    </div>
  );
}

/**
 * A resolution with no attack roll (`narration-ledger-retirement` 5.1): a Search, a Grapple break,
 * a thrown grenade, a mine going off, the GM's damage. The narrator's story line is the whole
 * collapsed card; the receipt grid opens under it, in the attack card's palette and grid, and the
 * warnings stay visible below. A story with no receipt rows (a deployed drone records no steps)
 * renders no toggle.
 */
export function EventCard({ story, receipt, warnings }: { story: string; receipt: readonly ReceiptRow[]; warnings?: readonly string[] }) {
  const [open, setOpen] = useState(false);
  const id = useId();
  return (
    <div className="ac ac-event" role="status">
      {receipt.length > 0 ? (
        <button type="button" className="ac-toggle" aria-expanded={open} aria-controls={id} onClick={() => setOpen((o) => !o)}>
          <span className="ac-event-story">{story}</span>
        </button>
      ) : (
        <span className="ac-event-story">{story}</span>
      )}
      {receipt.length > 0 && (
        <div className="ac-breakdown" id={id} hidden={!open}>
          <ReceiptGrid rows={receipt} />
        </div>
      )}
      <Warnings warnings={warnings} />
    </div>
  );
}
